import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";
import { Plus, UsersThree } from "@phosphor-icons/react";

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "read_only", department: "", phone: "" });

  const load = async () => {
    try {
      const [{ data }, { data: r }] = await Promise.all([api.get("/users"), api.get("/users/roles")]);
      setRows(data); setRoles(r.roles);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only super_admin / admin can manage users");
      else toast.error("Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", form); toast.success("User created"); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u._id}`, { active: !u.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u._id}`, { role }); toast.success("Role updated"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="users-page" className="page-fade">
      <PageHeader eyebrow="Admin" title="Users & roles" description="14 roles including Super Admin, Admin, Production, Design, Inventory, Purchase, Sales, Finance, Legal, Packaging, Shipping, Marketing, Customer Support and Read-Only." actions={
        user?.role === "super_admin" && <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-user-btn"><Plus size={14}/> New user</button>
      }/>
      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Department</th><th>2FA</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u._id} data-testid={`user-row-${u.email}`}>
                <td><div className="font-display text-[15px] font-semibold leading-snug">{u.name}</div></td>
                <td className="text-sm">{u.email}</td>
                <td>
                  <select value={u.role} onChange={(e)=>changeRole(u, e.target.value)} disabled={user?.role !== "super_admin" || u._id === user?._id}
                          className="input-cell max-w-[180px]" data-testid={`user-role-${u.email}`}>
                    {roles.map(r=><option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
                  </select>
                </td>
                <td className="text-sm text-muted-foreground">{u.department || "—"}</td>
                <td>{u.totp_enabled ? <StatusChip value="ok"/> : <span className="text-xs text-muted-foreground">off</span>}</td>
                <td>
                  {user?.role === "super_admin" && u._id !== user?._id ? (
                    <button onClick={()=>toggleActive(u)} className="text-xs underline underline-offset-2" data-testid={`user-toggle-${u.email}`}>{u.active ? "Disable" : "Enable"}</button>
                  ) : (
                    <StatusChip value={u.active ? "ok" : "cancelled"}/>
                  )}
                </td>
                <td className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                <td className="text-right whitespace-nowrap">{user?.role === "super_admin" && u._id !== user?._id && <RecordActions entity="users" record={u} frozen={["email"]} onChanged={load} />}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7}><EmptyState icon={UsersThree} title="No users yet" description="Invite team members to get started." /></td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-md" data-testid="new-user-form">
            <div className="label-eyebrow">New user</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            {[["name","Name"],["email","Email"],["password","Password"],["department","Department"],["phone","Phone"]].map(([k,l])=>(
              <label key={k} className="block mb-5">
                <span className="label-eyebrow">{l}</span>
                <input required={k==="name"||k==="email"||k==="password"} type={k==="password"?"password":"text"}
                       value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})}
                       className="input-underline" data-testid={`user-input-${k}`}/>
              </label>
            ))}
            <label className="block">
              <span className="label-eyebrow">Role</span>
              <select value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})}
                      className="input-soft mt-2" data-testid="user-input-role">
                {roles.map(r=><option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
              </select>
            </label>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="user-submit">Create user</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
